import { useState, useEffect } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { Card } from '@/primer/components';
import { Button, Input } from '@/primer/components';
import { Link } from '@/primer/components';
import Typography from '@/primer/components/Typography/Typography';
import { useAuth } from '@/api';
import { loginSchema, type LoginFormData } from '@/schemas';
import { getFieldError } from '@/utils';
import { PageLayout } from '@/components/layout';

/**
 * Validates and sanitizes a redirect URL to prevent open redirect attacks.
 * Only allows relative paths starting with '/'.
 */
function getSafeRedirectUrl(redirect: string | undefined): string {
  const defaultRedirect = '/dashboard';

  if (!redirect) {
    return defaultRedirect;
  }

  // Only allow relative paths that start with '/'
  // Reject absolute URLs, protocol-relative URLs (//), and other schemes
  if (redirect.startsWith('/') && !redirect.startsWith('//') && !redirect.includes(':')) {
    return redirect;
  }

  return defaultRedirect;
}

/**
 * Login — email + password authentication screen.
 *
 * Social login options live on the Landing screen. This screen handles
 * email/password credential entry only.
 *
 * The Influenster for Creators logo is rendered by PreAuthLayout — this
 * component owns only the card content.
 */
export function Login() {
  const [submitError, setSubmitError] = useState('');
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };

  const redirectTo = getSafeRedirectUrl(search.redirect);

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate({ to: redirectTo });
    }
  }, [isAuthenticated, isLoading, navigate, redirectTo]);

  const form = useForm({
    defaultValues: {
      email: '',
      password: '',
    } satisfies LoginFormData,
    onSubmit: async ({ value }) => {
      setSubmitError('');
      try {
        await login(value.email, value.password);
        navigate({ to: redirectTo });
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : 'Failed to login');
      }
    },
  });

  return (
    <PageLayout className="py-8" gridProps={{ className: 'px-5' }}>
      <Card translucent shadow="second">
        <div className="p-6 space-y-4">
          {/* Submit error */}
          {submitError && (
            <div className="bg-error border border-error rounded-small p-3">
              <Typography variant="body" size="small" color="error">
                {submitError}
              </Typography>
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="space-y-4"
          >
            {/* Email */}
            <form.Field name="email" validators={{ onBlur: loginSchema.shape.email }}>
              {(field) => (
                <Input
                  label="Email address"
                  type="email"
                  id={field.name}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="you@example.com"
                  error={
                    field.state.meta.isTouched ? getFieldError(field.state.meta.errors) : undefined
                  }
                  autoComplete="email"
                />
              )}
            </form.Field>

            {/* Password */}
            <form.Field name="password" validators={{ onBlur: loginSchema.shape.password }}>
              {(field) => (
                <Input
                  label="Password"
                  type="password"
                  id={field.name}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="••••••••"
                  error={
                    field.state.meta.isTouched ? getFieldError(field.state.meta.errors) : undefined
                  }
                  autoComplete="current-password"
                />
              )}
            </form.Field>

            {/* Legal copy */}
            <Typography variant="body" size="small" color="body" align="center" as="p">
              By signing up, you agree to our{' '}
              <Link to="/" variant="body" size="small">
                Terms &amp; Conditions
              </Link>{' '}
              and{' '}
              <Link to="/" variant="body" size="small">
                Privacy Policy
              </Link>
            </Typography>

            {/* Submit */}
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <Button type="submit" fullWidth isLoading={isSubmitting}>
                  Log In
                </Button>
              )}
            </form.Subscribe>
          </form>

          {/* Navigation link */}
          <Typography variant="body" size="small" color="bodylighter" align="center" as="p">
            Don&apos;t have an account?{' '}
            <Link to="/signup" variant="body" size="small">
              Sign up
            </Link>
          </Typography>
        </div>
      </Card>
    </PageLayout>
  );
}

export default Login;
